import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReChartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import MainCard from 'components/MainCard';
import { brand } from 'utils/designTokens';
import { useCurrency } from 'contexts/CurrencyContext';
import { formatCurrency } from 'utils/currency';

// Custom Tooltip component for Recharts
function CustomTooltip({ active, payload, label, isCurrency = false, currencyCode, currencySymbol }) {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: '#fff',
          p: 1.5,
          border: `1px solid ${brand.border}`,
          borderRadius: 2,
          boxShadow: brand.shadowMd
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((p, idx) => (
          <Typography key={idx} variant="caption" display="block" color={p.color} fontWeight={600}>
            {p.name}: {isCurrency 
              ? formatCurrency(p.value, currencyCode, currencySymbol) 
              : p.value}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
  isCurrency: PropTypes.bool,
  currencyCode: PropTypes.string,
  currencySymbol: PropTypes.string
};

// 1. Revenue Trend Chart (Area Chart)
export function RevenueTrendChart({ data, title = 'Revenue Trend' }) {
  const { currency } = useCurrency();
  return (
    <MainCard title={title} sx={{ border: `1px solid ${brand.border}` }}>
      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={brand.primary} stopOpacity={0.4} />
                <stop offset="95%" stopColor={brand.primary} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(31, 91, 64, 0.05)" />
            <XAxis 
              dataKey="date" 
              stroke="#8c8c8c" 
              fontSize={11} 
              tickLine={false} 
              axisLine={false} 
            />
            <YAxis 
              stroke="#8c8c8c" 
              fontSize={11} 
              tickLine={false} 
              axisLine={false} 
              tickFormatter={(v) => formatCurrency(v, currency.code, currency.symbol).split('.')[0]} 
            />
            <ReChartsTooltip 
              content={<CustomTooltip isCurrency currencyCode={currency.code} currencySymbol={currency.symbol} />} 
            />
            <Area 
              type="monotone" 
              dataKey="revenue" 
              name="Revenue" 
              stroke={brand.primary} 
              strokeWidth={2.5} 
              fillOpacity={1} 
              fill="url(#colorRevenue)" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </MainCard>
  );
}

RevenueTrendChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      revenue: PropTypes.number.isRequired
    })
  ).isRequired,
  title: PropTypes.string
};

// 2. Bar Chart (Generic for Store, Driver, etc.)
export function ReportBarChart({ data, xKey, yKey, name = 'Value', title, isCurrency = false }) {
  const { currency } = useCurrency();
  return (
    <MainCard title={title} sx={{ border: `1px solid ${brand.border}` }}>
      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(31, 91, 64, 0.05)" />
            <XAxis 
              dataKey={xKey} 
              stroke="#8c8c8c" 
              fontSize={11} 
              tickLine={false} 
              axisLine={false} 
            />
            <YAxis 
              stroke="#8c8c8c" 
              fontSize={11} 
              tickLine={false} 
              axisLine={false} 
              tickFormatter={(v) => isCurrency 
                ? formatCurrency(v, currency.code, currency.symbol).split('.')[0] 
                : v} 
            />
            <ReChartsTooltip 
              content={
                <CustomTooltip 
                  isCurrency={isCurrency} 
                  currencyCode={currency.code} 
                  currencySymbol={currency.symbol} 
                />
              } 
            />
            <Bar 
              dataKey={yKey} 
              name={name} 
              fill={brand.primaryLight} 
              radius={[4, 4, 0, 0]} 
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </MainCard>
  );
}

ReportBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  yKey: PropTypes.string.isRequired,
  name: PropTypes.string,
  title: PropTypes.string.isRequired,
  isCurrency: PropTypes.bool
};

// 3. Status Distribution Chart (Donut Chart)
const STATUS_COLORS = {
  pending: '#e6a23c',
  accepted: '#409eff',
  preparing: '#909399',
  ready: '#67c23a',
  pickedup: '#a94442',
  outfordelivery: '#8e44ad',
  served: '#11c15b',
  delivered: '#2ecc71',
  cancelled: '#f56c6c',
  rejected: '#d9534f'
};

export function StatusDistributionChart({ data, title = 'Status Distribution' }) {
  const chartData = Object.keys(data).map((key) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: Number(data[key]) || 0,
    color: STATUS_COLORS[key.toLowerCase().replace(/\s+/g, '')] || brand.primary
  })).filter((item) => item.value > 0);

  if (!chartData.length) {
    return (
      <MainCard title={title} sx={{ border: `1px solid ${brand.border}`, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No status data available.</Typography>
      </MainCard>
    );
  }

  return (
    <MainCard title={title} sx={{ border: `1px solid ${brand.border}` }}>
      <Box sx={{ width: '100%', height: 260, display: 'flex', alignItems: 'center' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={90}
              paddingAngle={3}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <ReChartsTooltip />
            <Legend verticalAlign="bottom" height={36} iconSize={10} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </MainCard>
  );
}

StatusDistributionChart.propTypes = {
  data: PropTypes.object.isRequired,
  title: PropTypes.string
};
